"use client";

import { useState, useContext } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
} from "@mui/material";
import { styled } from "@mui/system";
import axios from "axios";
import { AppStateContext } from "../../App";
import baseURL from "../../config/config";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import axiosInstance from "../../helpers/Axios";

const StyledBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 30vh;
  padding: 2rem;
  background-color: #f3f4f6;
  margin-top: 5rem;
`;

const FormContainer = styled(Box)`
  background-color: white;
  padding: 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 28rem;
`;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 0.75rem;
    margin: 1rem;
    max-height: 90vh;
  }
`;

const ResultCard = styled(Box)`
  background-color: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  background-color: #2563eb;
  &:hover {
    background-color: #1d4ed8;
  }
`;

const ResultsContainer = styled(Box)`
  max-height: 450px;
  overflow-y: auto;
  padding-right: 8px;
  margin-top: 16px;
  margin-bottom: 16px;
`;

const Testformimagepage = () => {
  const [imageZip, setImageZip] = useState(null);
  const [excelSheet, setExcelSheet] = useState(null);
  const [isDefault, setIsDefault] = useState(false);
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [open, setOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const { access_token } = useContext(AppStateContext);

  const handleImageZipChange = (event) => {
    setImageZip(event.target.files[0]);
  };

  const handleExcelSheetChange = (event) => {
    setExcelSheet(event.target.files[0]);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!excelSheet || !imageZip) {
      alert("Please upload both the Excel file and ZIP file.");
      return;
    }

    if (!access_token) {
      alert("Unauthorized! Please log in.");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("excel_file", excelSheet);
      formData.append("image_files", imageZip);
      formData.append("is_default", isDefault);

      const response = await axiosInstance.post(
        `${baseURL}/api/trail/bulk-image-upload/`,
        formData
      );

      setResponseData(response.data);
    } catch (error) {
      console.error("Error uploading files:", error);
      setResponseData({
        status: "error",
        message: "",
        errors: [],
        data: {
          message:
            error.response?.data?.message ||
            error.message ||
            "Upload failed! Please try again.",
        },
      });
    } finally {
      setLoading(false);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setImageZip(null);
    setExcelSheet(null);
    setResponseData(null);
    setTabValue(0);
  };

  const countErrorResults = () => {
    if (!responseData?.data?.results) return 0;
    return responseData.data.results.filter(result => result.image_status === "error").length;
  };

  const countProcessedResults = () => {
    if (!responseData?.data?.results) return 0;
    return responseData.data.results.filter(result => result.image_status === "processed").length;
  };

  const renderResultsList = () => {
    if (!responseData?.data?.results) return null;

    const filteredResults = responseData.data.results.filter(result => {
      if (tabValue === 0) return true; // All results
      if (tabValue === 1) return result.image_status === "processed"; // Processed results
      if (tabValue === 2) return result.image_status === "error"; // Error results
      return false;
    });

    return (
      <ResultsContainer>
        {filteredResults.map((result, index) => (
          <div
            key={index}
            className={`p-4 rounded-lg mb-3 border shadow-sm ${
              result.image_status === "error" 
                ? "bg-red-50 border-red-200" 
                : "bg-blue-50 border-blue-200"
            }`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                {result.image_status === "processed" ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                ) : (
                  <ErrorIcon className="h-5 w-5 text-red-500 mr-2" />
                )}
                <span className="font-medium text-gray-800">
                  {result.title}
                </span>
              </div>
              <span 
                className={`text-xs px-2 py-1 rounded-full font-medium ${
                  result.image_status === "error"
                    ? "bg-red-100 text-red-800"
                    : "bg-green-100 text-green-800"
                }`}>
                {result.image_status}
              </span>
            </div>
            
            {/* Error message */}
            {result.error && (
              <div className="bg-white bg-opacity-50 rounded p-3 mt-2 border-l-2 border-red-400">
                <span className="text-gray-600 text-sm font-medium block mb-1">
                  Error:
                </span>
                <div className="text-red-700 text-sm">
                  {result.error}
                </div>
              </div>
            )}

            {/* Images Found */}
            {result.images_found && result.images_found.length > 0 && (
              <div className="bg-white bg-opacity-50 rounded p-3 mt-2 border-l-2 border-green-400">
                <span className="text-gray-600 text-sm font-medium block mb-1">
                  Images Found ({result.images_found.length}):
                </span>
                <div className="flex flex-wrap gap-1">
                  {result.images_found.map((img, i) => (
                    <span key={i} className="px-2 py-1 bg-green-100 text-xs rounded text-green-800">
                      {img}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Images Missing */}
            {result.images_missing && result.images_missing.length > 0 && (
              <div className="bg-white bg-opacity-50 rounded p-3 mt-2 border-l-2 border-amber-400">
                <span className="text-gray-600 text-sm font-medium block mb-1">
                  Images Missing ({result.images_missing.length}):
                </span>
                <div className="flex flex-wrap gap-1">
                  {result.images_missing.map((img, i) => (
                    <span key={i} className="px-2 py-1 bg-amber-100 text-xs rounded text-amber-800">
                      {img}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Images Skipped */}
            {result.images_skipped && result.images_skipped.length > 0 && (
              <div className="bg-white bg-opacity-50 rounded p-3 mt-2 border-l-2 border-blue-400">
                <span className="text-gray-600 text-sm font-medium block mb-1">
                  Images Skipped ({result.images_skipped.length}):
                </span>
                <div className="flex flex-wrap gap-1">
                  {result.images_skipped.map((img, i) => (
                    <span key={i} className="px-2 py-1 bg-blue-100 text-xs rounded text-blue-800">
                      {img}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </ResultsContainer>
    );
  };

  return (
    <StyledBox>
      <FormContainer>
        <Typography
          variant="h4"
          className="text-center mb-6 font-semibold text-gray-800">
          Upload Bulk Images
        </Typography>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Image ZIP File
            </label>
            <TextField
              type="file"
              accept=".zip"
              onChange={handleImageZipChange}
              fullWidth
              variant="outlined"
              placeholder="Choose ZIP file"
              InputProps={{
                startAdornment: (
                  <ImageIcon className="w-5 h-5 text-gray-400 mr-2" />
                ),
              }}
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Excel Sheet
            </label>
            <TextField
              type="file"
              accept=".xlsx, .xls"
              onChange={handleExcelSheetChange}
              fullWidth
              variant="outlined"
              placeholder="Choose Excel file"
              InputProps={{
                startAdornment: (
                  <DescriptionIcon className="w-5 h-5 text-gray-400 mr-2" />
                ),
              }}
            />
          </div>

          {/* Toggle button for default setting */}
          <div className="py-2">
            <FormControlLabel
              control={
                <Switch
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" className="text-gray-700">
                  Set as default
                </Typography>
              }
            />
          </div>

          <StyledButton
            type="submit"
            variant="contained"
            fullWidth
            className="py-2.5"
            disabled={loading}>
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Upload Files"
            )}
          </StyledButton>
        </form>
      </FormContainer>

      <StyledDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <div className="relative">
          <div className="px-6 py-4 border-b">
            <div className="flex justify-between items-center">
              <Typography variant="h6" className="text-gray-800">
                Upload Results
              </Typography>
              <IconButton
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600">
                <CloseIcon />
              </IconButton>
            </div>
          </div>

          <DialogContent className="px-6 py-5">
            {responseData && (
              <div className="space-y-4">
                <div
                  className={`flex items-center space-x-3 ${
                    responseData.status === "success"
                      ? "bg-green-50 text-green-700"
                      : "bg-red-50 text-red-700"
                  } p-4 rounded-lg`}>
                  {responseData.status === "success" ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  ) : (
                    <ErrorIcon className="h-5 w-5 text-red-500" />
                  )}
                  <p className="font-medium">{responseData.data?.message}</p>
                </div>

                {responseData.status === "success" && responseData.data.results && (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <ResultCard>
                        <Typography variant="subtitle2" className="text-gray-500">
                          Total Items
                        </Typography>
                        <Typography variant="h6" className="text-gray-900">
                          {responseData.data.results.length}
                        </Typography>
                      </ResultCard>
                      <ResultCard>
                        <Typography variant="subtitle2" className="text-gray-500">
                          Processed
                        </Typography>
                        <Typography variant="h6" className="text-green-600">
                          {countProcessedResults()}
                        </Typography>
                      </ResultCard>
                      <ResultCard>
                        <Typography variant="subtitle2" className="text-gray-500">
                          Errors
                        </Typography>
                        <Typography variant="h6" className="text-red-600">
                          {countErrorResults()}
                        </Typography>
                      </ResultCard>
                      <ResultCard>
                        <Typography variant="subtitle2" className="text-gray-500">
                          Images Missing
                        </Typography>
                        <Typography variant="h6" className="text-amber-600">
                          {responseData.data.results.reduce((count, item) => 
                            count + (item.images_missing?.length || 0), 0)}
                        </Typography>
                      </ResultCard>
                    </div>

                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
                      <Tabs value={tabValue} onChange={handleTabChange} aria-label="result tabs">
                        <Tab label={`All Results (${responseData.data.results.length})`} />
                        <Tab label={`Processed (${countProcessedResults()})`} />
                        <Tab label={`Errors (${countErrorResults()})`} />
                      </Tabs>
                    </Box>

                    {renderResultsList()}
                  </>
                )}
              </div>
            )}
          </DialogContent>

          <div className="border-t px-6 py-4 bg-gray-50">
            <div className="flex justify-end">
              <StyledButton onClick={handleClose} variant="contained">
                Close
              </StyledButton>
            </div>
          </div>
        </div>
      </StyledDialog>
    </StyledBox>
  );
};

export default Testformimagepage;